import { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'
import {
  backstageModeration,
  type HeldComment,
  type HeldFile,
  type ReportedComment,
  type ReportedPost,
} from '../../services/backstageModeration'

type Tab = 'posts' | 'comments' | 'held-comments' | 'files'

const card =
  'rounded-2xl bg-white dark:bg-[#1a1425] shadow-sm border border-gray-100 dark:border-[#2d2236] p-4'
const alertClass =
  'rounded-xl bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-600 dark:text-red-400'
const meta = 'text-xs text-gray-500 dark:text-gray-400'
const errorMessage = (error: unknown) => (error instanceof Error ? error.message : 'Request failed')
const when = (value: string) => new Date(value).toLocaleString()

/** Several people can report the same item; staff decide once per item. */
function groupBy<T>(rows: T[], key: (row: T) => string) {
  const groups = new Map<string, T[]>()
  for (const row of rows) groups.set(key(row), [...(groups.get(key(row)) ?? []), row])
  return [...groups.values()]
}

function Reasons({ rows }: { rows: { id: string; reason: string; created_at: string }[] }) {
  return (
    <ul className="mt-3 space-y-1">
      {rows.map((row) => (
        <li key={row.id} className="text-sm text-gray-700 dark:text-gray-300">
          <span className="font-medium">Reported:</span> {row.reason}{' '}
          <span className={meta}>({when(row.created_at)})</span>
        </li>
      ))}
    </ul>
  )
}

export function BackstageModeration() {
  const canEdit = usePermission('shops', 'edit')
  const [tab, setTab] = useState<Tab>('posts')
  const [posts, setPosts] = useState<ReportedPost[]>([])
  const [comments, setComments] = useState<ReportedComment[]>([])
  const [held, setHeld] = useState<HeldComment[]>([])
  const [files, setFiles] = useState<HeldFile[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [p, c, h, f] = await Promise.all([
        backstageModeration.postReports(),
        backstageModeration.commentReports(),
        backstageModeration.heldComments(),
        backstageModeration.heldFiles(),
      ])
      setPosts(p)
      setComments(c)
      setHeld(h)
      setFiles(f)
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (id: string, run: () => Promise<unknown>) => {
    setBusy(id)
    setError(null)
    try {
      await run()
      await load()
    } catch (cause) {
      setError(errorMessage(cause))
    } finally {
      setBusy(null)
    }
  }

  const postGroups = useMemo(() => groupBy(posts, (row) => row.post_id), [posts])
  const commentGroups = useMemo(() => groupBy(comments, (row) => row.comment_id), [comments])

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'posts', label: 'Reported posts', count: postGroups.length },
    { key: 'comments', label: 'Reported comments', count: commentGroups.length },
    { key: 'held-comments', label: 'Held comments', count: held.length },
    { key: 'files', label: 'Held member files', count: files.length },
  ]

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">
          Backstage moderation
        </h2>
        <CustomButton variant="secondary" size="sm" onClick={() => void load()} loading={loading}>
          Refresh
        </CustomButton>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="Moderation queues">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              tab === item.key
                ? 'bg-[#7f4f9a] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-[#2d2236] dark:text-gray-200'
            }`}
          >
            {item.label} ({item.count})
          </button>
        ))}
      </div>

      {error && <div className={`mb-6 ${alertClass}`}>{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-4">
          {tab === 'posts' &&
            (postGroups.length === 0 ? (
              <p className="py-12 text-center text-gray-500">No open post reports</p>
            ) : (
              postGroups.map((rows) => {
                const post = rows[0].backstage_posts
                const id = rows[0].post_id
                return (
                  <div key={id} className={card}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {post?.title || 'Untitled post'}
                        </p>
                        <p className={meta}>
                          {post?.post_type} · {post?.visibility === 'subscriber' ? 'members only' : 'public'} ·
                          shop {post?.shop_id} · {post?.status} · {rows.length}{' '}
                          {rows.length === 1 ? 'report' : 'reports'}
                        </p>
                        {post?.body && (
                          <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                            {post.body}
                          </p>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex shrink-0 gap-2">
                          <CustomButton
                            variant="danger"
                            size="sm"
                            loading={busy === id}
                            onClick={() => void act(id, () => backstageModeration.decidePost(id, 'remove'))}
                          >
                            Remove post
                          </CustomButton>
                          <CustomButton
                            variant="secondary"
                            size="sm"
                            disabled={busy === id}
                            onClick={() => void act(id, () => backstageModeration.decidePost(id, 'dismiss'))}
                          >
                            Dismiss
                          </CustomButton>
                        </div>
                      )}
                    </div>
                    <Reasons rows={rows} />
                  </div>
                )
              })
            ))}

          {tab === 'comments' &&
            (commentGroups.length === 0 ? (
              <p className="py-12 text-center text-gray-500">No open comment reports</p>
            ) : (
              commentGroups.map((rows) => {
                const comment = rows[0].backstage_comments
                const id = rows[0].comment_id
                return (
                  <div key={id} className={card}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
                          {comment?.body}
                        </p>
                        <p className={meta}>
                          post {comment?.post_id} · author {comment?.author_id} · {comment?.status} ·{' '}
                          {rows.length} {rows.length === 1 ? 'report' : 'reports'}
                        </p>
                      </div>
                      {canEdit && (
                        <div className="flex shrink-0 gap-2">
                          <CustomButton
                            variant="danger"
                            size="sm"
                            loading={busy === id}
                            onClick={() =>
                              void act(id, () => backstageModeration.decideReportedComment(id, 'remove'))
                            }
                          >
                            Remove comment
                          </CustomButton>
                          <CustomButton
                            variant="secondary"
                            size="sm"
                            disabled={busy === id}
                            onClick={() =>
                              void act(id, () => backstageModeration.decideReportedComment(id, 'dismiss'))
                            }
                          >
                            Dismiss
                          </CustomButton>
                        </div>
                      )}
                    </div>
                    <Reasons rows={rows} />
                  </div>
                )
              })
            ))}

          {tab === 'held-comments' &&
            (held.length === 0 ? (
              <p className="py-12 text-center text-gray-500">No comments waiting for review</p>
            ) : (
              held.map((comment) => (
                <div key={comment.id} className={`${card} flex items-start justify-between gap-4`}>
                  <div className="min-w-0">
                    <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">{comment.body}</p>
                    <p className={meta}>
                      post {comment.post_id} · author {comment.author_id} · {when(comment.created_at)}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex shrink-0 gap-2">
                      <CustomButton
                        variant="success"
                        size="sm"
                        loading={busy === comment.id}
                        onClick={() =>
                          void act(comment.id, () => backstageModeration.decideHeldComment(comment.id, 'approve'))
                        }
                      >
                        Approve
                      </CustomButton>
                      <CustomButton
                        variant="danger"
                        size="sm"
                        disabled={busy === comment.id}
                        onClick={() =>
                          void act(comment.id, () => backstageModeration.decideHeldComment(comment.id, 'remove'))
                        }
                      >
                        Remove
                      </CustomButton>
                    </div>
                  )}
                </div>
              ))
            ))}

          {tab === 'files' &&
            (files.length === 0 ? (
              <p className="py-12 text-center text-gray-500">No member files waiting for review</p>
            ) : (
              files.map((file) => (
                <div key={file.id} className={`${card} flex items-start justify-between gap-4`}>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white">{file.filename}</p>
                    <p className={meta}>
                      {file.mime_type} · {(file.size_bytes / 1024 / 1024).toFixed(1)} MB · shop {file.shop_id} ·{' '}
                      {when(file.created_at)}
                    </p>
                  </div>
                  {canEdit && (
                    <div className="flex shrink-0 gap-2">
                      <CustomButton
                        variant="secondary"
                        size="sm"
                        disabled={busy === file.id}
                        onClick={() => {
                          // Open the tab inside the click, or the browser blocks it as a popup.
                          const preview = window.open('', '_blank')
                          void act(file.id, async () => {
                            try {
                              const url = await backstageModeration.previewFile(file.id)
                              if (preview) {
                                preview.opener = null
                                preview.location.href = url
                              }
                            } catch (cause) {
                              preview?.close()
                              throw cause
                            }
                          })
                        }}
                      >
                        Preview
                      </CustomButton>
                      <CustomButton
                        variant="success"
                        size="sm"
                        loading={busy === file.id}
                        onClick={() => void act(file.id, () => backstageModeration.decideFile(file.id, 'approve'))}
                      >
                        Approve
                      </CustomButton>
                      <CustomButton
                        variant="danger"
                        size="sm"
                        disabled={busy === file.id}
                        onClick={() => void act(file.id, () => backstageModeration.decideFile(file.id, 'remove'))}
                      >
                        Block
                      </CustomButton>
                    </div>
                  )}
                </div>
              ))
            ))}
        </div>
      )}
    </div>
  )
}
